import React, { useCallback, useEffect, useState } from 'react';
import { Image as ImageIcon, Trash2, Type } from 'lucide-react';
import {
  getFirmBranding,
  saveFirmBrandingText,
  saveFirmBrandingLogo,
  clearFirmBranding,
} from '../lib/firmBranding';
import ReportBrandMark from '../../reports/components/ReportBrandMark';

// Both caps mirror the server's (api_manual.py): rejecting here turns a 400 into
// an immediate message instead of a round trip through a 1 MB upload.
const TEXT_MAX = 300;
const LOGO_MAX_BYTES = 1_000_000;
const LOGO_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

/**
 * "Report Branding" - Manual section widget.
 *
 * One mark per firm: custom text OR a logo image (never both - the server clears
 * the other). It prints in the top left of page 1 of every report every user of
 * the firm generates. Backend: /api/manual/firm-branding.
 */
const FirmBrandingPanel = ({ onBack, fetchWithAuth, darkMode }) => {
  const [branding, setBranding] = useState(null);
  const [mode, setMode] = useState('text'); // 'text' | 'logo'
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const card = darkMode ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200';
  const input = `px-2 py-1 rounded border text-sm ${darkMode ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300'}`;

  // Server truth in, widget state out - the mode follows what is actually set,
  // so the widget opens on the branch the firm is using.
  const apply = useCallback((data) => {
    setBranding(data);
    setText(data?.text || '');
    setMode(data?.logo_data_url ? 'logo' : 'text');
  }, []);

  useEffect(() => {
    let mounted = true;
    getFirmBranding(fetchWithAuth)
      .then((data) => { if (mounted) apply(data); })
      .catch((e) => { if (mounted) setError(e.message || 'Failed to load branding'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [fetchWithAuth, apply]);

  const run = async (mutation, message) => {
    setSaving(true); setError(null); setNotice(null);
    try {
      apply(await mutation());
      setNotice(message);
    } catch (e) {
      setError(e.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const saveText = () => {
    const value = text.trim();
    if (!value) { setError('Enter the text to print on your reports.'); return; }
    run(() => saveFirmBrandingText(fetchWithAuth, value), 'Branding text saved');
  };

  const onPickLogo = (e) => {
    const file = e.target.files && e.target.files[0];
    // Clear the selection so the SAME file can be picked again after a rejection.
    e.target.value = '';
    if (!file) return;
    setError(null); setNotice(null);
    if (file.size > LOGO_MAX_BYTES) {
      setError('That image is larger than 1 MB - please upload a smaller logo.');
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setError('Could not read that image file.');
    reader.onload = () => {
      const b64 = String(reader.result).split(',')[1] || '';
      run(() => saveFirmBrandingLogo(fetchWithAuth, b64), 'Logo saved');
    };
    reader.readAsDataURL(file);
  };

  const hasBranding = !!(branding && (branding.text || branding.logo_data_url));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={onBack} className={`text-sm ${muted} hover:underline`}>&larr; Manual</button>
          <h4 className="text-xl font-semibold mt-1">Report Branding</h4>
          <p className={`text-sm ${muted}`}>
            Printed in the top left of page 1 of every generated report, for all users of your firm.
          </p>
        </div>
      </div>

      {error && <div className="mb-3 px-3 py-2 rounded bg-red-100 text-red-800 text-sm">{error}</div>}
      {notice && <div className="mb-3 px-3 py-2 rounded bg-green-100 text-green-800 text-sm">{notice}</div>}

      {loading ? <p className={muted}>Loading...</p> : (
        <div className="max-w-2xl">
          {/* Preview on white, because the report page is always white - this is
              the same component the report itself renders. */}
          <div className={`p-4 rounded-lg border mb-4 ${card}`}>
            <div className={`text-xs font-semibold uppercase ${muted} mb-2`}>Preview</div>
            <div className="bg-white rounded p-4" data-testid="branding-preview">
              {hasBranding ? <ReportBrandMark brand={branding} /> : (
                <p className="text-sm text-gray-500">No branding set - reports print without a mark.</p>
              )}
            </div>
            {hasBranding && (
              <button
                onClick={() => run(() => clearFirmBranding(fetchWithAuth), 'Branding removed')}
                disabled={saving}
                className="mt-3 flex items-center gap-1 text-sm text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" /> Remove branding
              </button>
            )}
          </div>

          <div className="flex gap-2 mb-4">
            {[
              { k: 'text', label: 'Custom text', icon: Type },
              { k: 'logo', label: 'Logo image', icon: ImageIcon },
            ].map((m) => (
              <button
                key={m.k}
                onClick={() => setMode(m.k)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${
                  mode === m.k
                    ? 'bg-teal-600 text-white border-teal-600'
                    : darkMode ? 'border-white/10 text-gray-300' : 'border-gray-300 text-gray-700'
                }`}
              >
                <m.icon className="w-4 h-4" /> {m.label}
              </button>
            ))}
          </div>

          {mode === 'text' ? (
            <div className={`flex flex-wrap items-end gap-2 p-3 rounded-lg border ${card}`}>
              <div className="flex-1 min-w-[220px]">
                <label className={`block text-xs ${muted}`} htmlFor="branding-text">Branding text</label>
                <input
                  id="branding-text"
                  className={`${input} w-full`}
                  maxLength={TEXT_MAX}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Banyan Tree Asset Management"
                />
              </div>
              <button
                onClick={saveText}
                disabled={saving}
                className="px-3 py-1.5 rounded bg-teal-600 text-white text-sm disabled:opacity-50"
              >
                Save
              </button>
            </div>
          ) : (
            <div className={`p-3 rounded-lg border ${card}`}>
              <label className={`block text-xs ${muted}`} htmlFor="branding-logo">Logo image</label>
              <input
                id="branding-logo"
                data-testid="branding-logo-input"
                type="file"
                accept={LOGO_ACCEPT}
                onChange={onPickLogo}
                disabled={saving}
                className="text-sm mt-1"
              />
              <p className={`text-xs mt-2 ${muted}`}>PNG, JPEG, GIF or WEBP, up to 1 MB. Saved as soon as you choose a file.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FirmBrandingPanel;
